import { cn } from "@/lib/utils";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";

export const initials = (name?: string | null) =>
  (name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();

const PALETTE = [
  "hsl(var(--cc-burnt))",
  "hsl(var(--cc-terracotta))",
  "hsl(var(--cc-gold))",
  "hsl(var(--cc-forest))",
  "hsl(var(--cc-clay))",
  "hsl(var(--cc-sunset))",
];
export const colorFromId = (id?: string | null) => {
  if (!id) return PALETTE[0];
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  return PALETTE[h % PALETTE.length];
};

export function CcAvatar({
  name,
  src,
  id,
  size = 40,
  className,
}: {
  name?: string | null;
  src?: string | null;
  id?: string | null;
  size?: number;
  className?: string;
}) {
  const bg = colorFromId(id || name);
  return (
    <Avatar
      className={cn("ring-1 ring-cc-border", className)}
      style={{ width: size, height: size }}
    >
      {src && <AvatarImage src={src} alt={name || ""} />}
      <AvatarFallback
        style={{ background: bg, color: "hsl(var(--cc-cream))", fontWeight: 600 }}
      >
        {initials(name)}
      </AvatarFallback>
    </Avatar>
  );
}

export function PresenceDot({ online, className }: { online?: boolean; className?: string }) {
  return (
    <span
      className={cn(
        "inline-block h-2.5 w-2.5 rounded-full ring-2 ring-cc-surface",
        online ? "bg-cc-forest" : "bg-cc-ink-soft/40",
        className,
      )}
      aria-hidden
    />
  );
}

export function StatusRing({
  seen,
  size = 56,
  children,
}: {
  seen?: boolean;
  size?: number;
  children: React.ReactNode;
}) {
  return (
    <span
      className={cn("chitchat-status-ring inline-flex items-center justify-center", seen && "chitchat-status-ring--seen")}
      style={{ width: size, height: size }}
    >
      <span className="bg-cc-surface rounded-full p-[2px] flex items-center justify-center w-full h-full">
        {children}
      </span>
    </span>
  );
}

export function CcEmpty({
  icon,
  title,
  hint,
  action,
}: {
  icon: React.ReactNode;
  title: string;
  hint?: string;
  action?: React.ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center text-center p-8 gap-3 chitchat-pattern-soft rounded-2xl">
      <div className="h-16 w-16 rounded-full chitchat-brand-bg flex items-center justify-center shadow-md">
        {icon}
      </div>
      <h3 className="text-base font-semibold text-cc-ink">{title}</h3>
      {hint && <p className="text-sm text-cc-ink-soft max-w-xs">{hint}</p>}
      {action}
    </div>
  );
}

export function AfroDivider({ className }: { className?: string }) {
  return (
    <div className={cn("flex items-center gap-2 text-cc-ink-soft", className)}>
      <span className="h-px flex-1 bg-cc-border" />
      <svg width="36" height="10" viewBox="0 0 36 10" fill="none" aria-hidden>
        <path d="M0 5 L6 0 L12 5 L18 0 L24 5 L30 0 L36 5" stroke="hsl(var(--cc-burnt))" strokeWidth="1.2" />
      </svg>
      <span className="h-px flex-1 bg-cc-border" />
    </div>
  );
}
